const irregularPlurals: Record<string, string> = {
  child: 'children',
  person: 'people',
  man: 'men',
  woman: 'women',
  tooth: 'teeth',
  foot: 'feet',
  mouse: 'mice',
  goose: 'geese',
  cactus: 'cacti',
  fungus: 'fungi',
  nucleus: 'nuclei',
  radius: 'radii',
  stimulus: 'stimuli',
  vertex: 'vertices',
  index: 'indices',
  matrix: 'matrices',
  axis: 'axes',
  crisis: 'crises',
  phenomenon: 'phenomena',
  criterion: 'criteria',
  ox: 'oxen',
  die: 'dice',
  pro: 'pros',
}

// Exceptions for words ending in "o" that simply take an "s"
const oExceptions = new Set([
  'photo',
  'piano',
  'halo',
  'memo',
  'canto',
  'soprano',
  'kangaroo',
  'studio',
  'video',
  'radio',
])

// Exceptions for words ending in "f" or "fe" that simply take an "s"
const fExceptions = new Set(['roof', 'belief', 'chef', 'cliff'])

/**
 * Pluralizes a word based on English grammar rules
 * @param word - The word to pluralize
 * @param count - The count to determine if pluralization is needed (default: 2)
 * @returns The pluralized word if count !== 1, otherwise the original word
 */
export function pluralize(word: string, count: number = 2): string {
  if (count === 1) {
    return word
  }

  // Check if the lowercased word exists in our irregular map
  const lowerWord = word.toLowerCase()
  if (irregularPlurals[lowerWord]) {
    return matchCase(word, irregularPlurals[lowerWord])
  }

  let plural: string

  // Rule 1: If the word ends with "y" preceded by a non-vowel, replace "y" with "ies"
  if (word.endsWith('y') && word.length > 1 && !isVowel(word[word.length - 2])) {
    plural = `${word.substring(0, word.length - 1)}ies`
  }
  // Rule 2: If the word ends with "o"
  else if (word.endsWith('o')) {
    if (oExceptions.has(lowerWord)) {
      plural = `${word}s`
    } else {
      plural = `${word}es`
    }
  }
  // Rule 3: If the word ends with "fe", replace with "ves"
  else if (word.endsWith('fe')) {
    if (fExceptions.has(lowerWord)) {
      plural = `${word}s`
    } else {
      plural = `${word.substring(0, word.length - 2)}ves`
    }
  }
  // Rule 4: If the word ends with "f", replace with "ves"
  else if (word.endsWith('f')) {
    if (fExceptions.has(lowerWord)) {
      plural = `${word}s`
    } else {
      plural = `${word.substring(0, word.length - 1)}ves`
    }
  }
  // Rule 5: If the word ends with sibilant sounds (s, sh, ch, x, or z), append "es"
  else if (
    word.endsWith('s') ||
    word.endsWith('sh') ||
    word.endsWith('ch') ||
    word.endsWith('x') ||
    word.endsWith('z')
  ) {
    plural = `${word}es`
  }
  // Default rule: Just append "s"
  else {
    plural = `${word}s`
  }

  return matchCase(word, plural)
}

/**
 * Helper method to determine if a letter is a vowel
 * @param letter - The letter to check
 * @returns true if the letter is a vowel, false otherwise
 */
function isVowel(letter: string): boolean {
  return 'aeiou'.includes(letter.toLowerCase())
}

/**
 * Helper to preserve the original capitalization:
 * - If the entire original is uppercase, return an uppercase result
 * - If only the first letter is uppercase, capitalize the first letter of the result
 * - Otherwise, return the result as is
 * @param original - The original word
 * @param result - The pluralized word
 * @returns The pluralized word with proper capitalization
 */
function matchCase(original: string, result: string): string {
  if (original === original.toUpperCase()) {
    return result.toUpperCase()
  } else if (original[0] === original[0].toUpperCase()) {
    return result[0].toUpperCase() + result.substring(1)
  }
  return result
}
